import React from "react";
import { Badge } from "@teals/ui-web";
import type { DataSource } from "../../store/api/dataSourcesApi";
import {
  DS_TYPE_LABELS,
  DS_TYPE_BADGE_VARIANT,
  BYO_PROVIDER_LABELS,
} from "./constants";
import {
  DSCard,
  CardHeader,
  CardTitle,
  CardRecords,
  AgentChipRow,
  AgentChip,
  CardFooter,
  MetaItem,
  DividerDot,
} from "./styles";

interface DataSourceCardProps {
  ds: DataSource;
  onClick: () => void;
}

function formatRelativeTime(isoDate: string | null): string {
  if (!isoDate) return "Never synced";

  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return "just now";

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({ ds, onClick }) => {
  const isByo = ds.dsType === "byo";

  const recordLabel =
    ds.recordCount != null && ds.recordCount > 0
      ? `${ds.recordCount.toLocaleString()} records`
      : "No records";

  const indexName = isByo
    ? (ds.connectionConfig?.index_name as string | undefined)
    : undefined;
  const providerLabel = ds.provider
    ? (BYO_PROVIDER_LABELS[ds.provider] ?? ds.provider)
    : null;

  return (
    <DSCard padding={20} elevation="sm" onClick={onClick}>
      <CardHeader>
        <div>
          <CardTitle>{ds.name}</CardTitle>
          <CardRecords>{recordLabel}</CardRecords>
        </div>
        <Badge variant={DS_TYPE_BADGE_VARIANT[ds.dsType] as any} size="sm">
          {DS_TYPE_LABELS[ds.dsType]}
        </Badge>
      </CardHeader>

      {ds.agentIds.length > 0 && (
        <AgentChipRow>
          {ds.agentIds.map((id) => (
            <AgentChip key={id}>{id}</AgentChip>
          ))}
        </AgentChipRow>
      )}

      <CardFooter>
        {isByo && providerLabel ? (
          <MetaItem>
            {providerLabel}
            {indexName ? ` · ${indexName}` : ""}
          </MetaItem>
        ) : (
          <MetaItem>Org: {ds.orgName}</MetaItem>
        )}
        <DividerDot />
        <MetaItem>{formatRelativeTime(ds.updatedAt)}</MetaItem>
      </CardFooter>
    </DSCard>
  );
};

export default DataSourceCard;
